import { useEffect, useRef, useCallback, useState } from "react";

/**
 * Optimized version of useMicVolume that provides real-time volume data
 * Returns the current volume percentage for React components to use
 */
export default function useOptimizedMicVolume(microphoneStream: MediaStream | null) {
  const analyserRef = useRef<AnalyserNode | null>(null);
  const lastVolumeRef = useRef(20);
  const audioContextRef = useRef<AudioContext | null>(null);
  const [volume, setVolume] = useState(20);

  const updateVolume = useCallback(() => {
    if (!analyserRef.current) return;

    try {
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);
      const avg = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;

      const sensitivityMultiplier = 5;
      let percent = Math.min(
        100,
        Math.round((avg / 256) * 100 * sensitivityMultiplier)
      );
      percent = Math.max(percent, 0);

      // Update the ref for immediate access
      lastVolumeRef.current = percent;
      
      // Update state to trigger React re-render
      setVolume(percent);
    } catch (error) {
      console.warn('Error updating volume meter:', error);
      // Set a default volume if there's an error
      setVolume(20);
    }
  }, []);

  useEffect(() => {
    if (!microphoneStream) {
      setVolume(20);
      return;
    }

    try {
      // Create audio context only once
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }
      const audioCtx = audioContextRef.current;

      // Resume audio context if it's suspended (required by some browsers)
      if (audioCtx.state === 'suspended') {
        audioCtx.resume().catch(console.warn);
      }

      const source = audioCtx.createMediaStreamSource(microphoneStream);

      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8; // Add smoothing for better visual feedback
      analyserRef.current = analyser;

      source.connect(analyser);

      let animationFrameId: number;
      let isActive = true;

      const startVolumeMonitoring = () => {
        if (!isActive) return;
        updateVolume();
        animationFrameId = requestAnimationFrame(startVolumeMonitoring);
      };

      startVolumeMonitoring();

      return () => {
        isActive = false;
        cancelAnimationFrame(animationFrameId);
        try {
          analyser.disconnect();
          source.disconnect();
        } catch (error) {
          console.warn('Error disconnecting audio nodes:', error);
        }
      };
    } catch (error) {
      console.warn('Error setting up volume monitoring:', error);
      setVolume(20);
    }
  }, [microphoneStream, updateVolume]);

  // Clean up audio context when component unmounts
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  // Return the current volume for React components
  return volume;
}